import { FileText } from 'lucide-react';
import { cn } from '@/utils/cn';
import { DiffStat } from './DiffStat';
import type { DiffSectionKind, DiffStats } from './types';

interface SidebarSection {
  id: string;
  label: string;
  stats: DiffStats;
  kind: DiffSectionKind;
}

interface CompareSidebarProps {
  sections: SidebarSection[];
  onNavigate: (id: string) => void;
  onlyChanges: boolean;
}

const LEGEND: [string, string, string][] = [
  ['bg-emerald-100 text-emerald-700', '+', 'přidáno'],
  ['bg-red-100 text-red-700', '−', 'odebráno'],
  ['bg-amber-100 text-amber-700', '~', 'změněno'],
];

export const CompareSidebar = ({ sections, onNavigate, onlyChanges }: CompareSidebarProps) => (
  <nav className="w-[248px] shrink-0 border-r border-neutral-150 bg-neutral-25 overflow-y-auto py-2">
    <div className="px-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-neutral-500">
      {onlyChanges ? 'Změněné části' : 'Všechny části'}
    </div>
    {sections.map((sec) => (
      <button
        key={sec.id}
        type="button"
        onClick={() => onNavigate(sec.id)}
        data-testid={`compare-section-nav-${sec.id}`}
        className="w-full text-left px-3 py-1.5 hover:bg-white flex items-start gap-2 group"
      >
        <FileText
          size={13}
          className="mt-0.5 text-neutral-400 group-hover:text-neutral-600 shrink-0"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] text-neutral-800 leading-snug">{sec.label}</span>
          {sec.stats.add + sec.stats.del + sec.stats.mod === 0 ? (
            <span className="block text-xs text-neutral-500">Beze změn</span>
          ) : (
            <DiffStat
              {...sec.stats}
              unit={sec.kind === 'text' ? 'řádky textu' : 'položky'}
              className="mt-0.5"
            />
          )}
        </span>
      </button>
    ))}
    <div className="mt-2 pt-2 border-t border-neutral-150 px-3 space-y-1.5">
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-500">
        Legenda
      </div>
      {LEGEND.map(([color, sign, label]) => (
        <div key={sign} className="flex items-center gap-2 text-[11.5px] text-neutral-600">
          <span
            className={cn(
              'h-4 w-4 rounded-[3px] inline-flex items-center justify-center font-mono text-[11px]',
              color
            )}
          >
            {sign}
          </span>
          {label}
        </div>
      ))}
    </div>
  </nav>
);
